import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import * as adminService from "@/shared/api/services/adminService";
import * as providerService from "@/shared/api/services/providerService";
import * as transactionService from "@/shared/api/services/transactionService";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetailPanel from "@/shared/components/admin/TransactionDetailPanel";
import { exportToCsv } from "@/shared/utils/csvExport";
import { Button } from "@/shared/components/ui/button";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { ChevronLeft, ChevronRight, ArrowUpFromLine, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { mapGlobalTransactions } from "@/shared/utils/transactionMappers";
import { useMemo, useEffect } from "react";

export default function Withdrawals() {
  const { t, language } = useLanguage();
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    engine: "all",
    reseller: "all",
    merchant: "all",
    provider: "all",
    currency: "all",
    location: "all",
    dateFrom: undefined,
    dateTo: undefined,
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const pageSize = 10;
  const [merchantIds, setMerchantIds] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  const { data: paginationResult, isLoading: txLoading, refetch: refetchTransactions, error: transactionsError } = useQuery({
    queryKey: ["admin-global-transactions-withdrawals", page, pageSize, search, filters, merchantIds],
    queryFn: () => {
      const backendFilters = { type: "withdrawal" };
      if (filters.status !== "all") backendFilters.status = filters.status;
      if (filters.provider !== "all") backendFilters.provider = filters.provider;
      if (filters.currency !== "all") backendFilters.currency = filters.currency;
      if (filters.location !== "all") backendFilters.location = filters.location;
      if (filters.dateFrom) backendFilters.startDate = filters.dateFrom;
      if (filters.dateTo) {
        const endOfToDay = new Date(filters.dateTo);
        endOfToDay.setHours(23, 59, 59, 999);
        backendFilters.endDate = endOfToDay.toISOString();
      }

      return transactionService.getPaginatedTransactions(
        page + 1,
        pageSize,
        search,
        backendFilters,
        merchantIds
      );
    },
    refetchInterval: 5000,
    keepPreviousData: true,
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  const { data: engines = [] } = useQuery({
    queryKey: ["engines"],
    queryFn: () => adminService.getAllEngines(),
  });

  const { data: resellers = [] } = useQuery({
    queryKey: ["resellers"],
    queryFn: () => adminService.getAllResellers(),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  const { data: uniqueProviderNames = [] } = useQuery({
    queryKey: ["all-transaction-providers"],
    queryFn: () => transactionService.getUniqueProviders(),
  });

  const rawTransactions = paginationResult?.data || [];
  const totalTransactions = paginationResult?.total || 0;
  const totalPagesCount = paginationResult?.totalPages || 1;

  const combinedData = useMemo(() => {
    return mapGlobalTransactions(rawTransactions);
  }, [rawTransactions]);

  React.useEffect(() => {
    const hasEntityFilters =
      filters.engine !== "all" ||
      filters.reseller !== "all" ||
      filters.merchant !== "all";

    if (!hasEntityFilters) {
      setMerchantIds([]);
      return;
    }

    const merchantIdSet = new Set();

    if (filters.engine !== "all") {
      const resellerIdsUnderEngine = resellers
        .filter((r) => r.createdByEngineId === filters.engine)
        .map((r) => r.id);

      merchants
        .filter((m) => m.createdByEngineId === filters.engine || resellerIdsUnderEngine.includes(m.createdByResellerId))
        .forEach((m) => merchantIdSet.add(m.id));
    }

    if (filters.reseller !== "all") {
      merchants
        .filter((m) => m.createdByResellerId === filters.reseller)
        .forEach((m) => merchantIdSet.add(m.id));
    }

    if (filters.merchant !== "all") {
      merchantIdSet.add(filters.merchant);
    }

    const nextMerchantIds = Array.from(merchantIdSet);
    const normalizedIds = nextMerchantIds.length > 0 ? nextMerchantIds : ["NO_MERCHANT_FOUND"];

    setMerchantIds((prev) => {
      if (prev.length === normalizedIds.length && prev.every((id, index) => id === normalizedIds[index])) {
        return prev;
      }
      return normalizedIds;
    });
  }, [filters.engine, filters.reseller, filters.merchant, merchants, resellers]);

  const providerOptions = useMemo(() => {
    const options = [];
    const seen = new Set();

    const addOption = (value, label, aliases = []) => {
      const normalizedValue = String(value || "").trim();
      if (!normalizedValue) return;
      if (seen.has(normalizedValue)) return;

      seen.add(normalizedValue);
      options.push({
        value: normalizedValue,
        label: label || normalizedValue,
        aliases: [normalizedValue, ...(aliases || [])].filter(Boolean),
      });
    };

    providers.forEach((p) => {
      const label = p.nameEn || p.name || p.code;
      addOption(p.id, label, [p.code, p.name, p.nameEn]);
    });

    combinedData.forEach((tx) => {
      const name = tx.client_bank_name || tx.bank_name || tx.provider_code || tx.bank_code;
      const provider = providers.find(p =>
        p.code === tx.provider_code ||
        p.name === name ||
        p.nameEn === name ||
        p.code === tx.bank_code
      );
      const value = provider ? provider.id : name;
      addOption(value, name, [tx.provider_code, tx.bank_code]);
    });

    uniqueProviderNames.forEach((entry) => {
      const name = typeof entry === "string"
        ? entry
        : (entry?.bankName || entry?.name || entry?.provider || entry?.value);

      const provider = providers.find(p =>
        p.name === name ||
        p.nameEn === name ||
        p.code === name
      );
      const value = provider ? provider.id : name;
      addOption(value, name);
    });

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [providers, combinedData, uniqueProviderNames]);

  const paginatedTransactions = combinedData;

  const totalPages = totalPagesCount;

  const stats = useMemo(() => {
    const success = combinedData.filter((t) => t.status === "success");
    const pending = combinedData.filter((t) => t.status === "pending" || t.status === "processing");
    const declined = combinedData.filter((t) => t.status === "failed" || t.status === "declined");
    return {
      total: success.reduce((sum, t) => sum + (t.ammount || t.amount || 0), 0),
      count: totalTransactions,
      successCount: success.length,
      pendingCount: pending.length,
      declinedCount: declined.length,
    };
  }, [combinedData, totalTransactions]);

  // CSV export
  const WITHDRAWAL_CSV_HEADERS = [
    "id",
    "transaction_id",
    "payment_id",
    "ammount",
    "fee_amount",
    "net_ammount",
    "status",
    "currency",
    "merchant_id",
    "merchant_name",
    "bank_code",
    "bank_account",
    "account_name",
    "customer_name",
    "client_bank_name",
    "network",
    "wallet_address",
    "createdAt",
    "updatedAt",
    "type",
  ];

  const getWithdrawalCellValue = (row, key) => {
    if (key === "merchant_name") return row.merchant?.name;
    return row[key];
  };

  const handleExportCsv = () => {
    exportToCsv(combinedData, {
      headers: WITHDRAWAL_CSV_HEADERS,
      filename: `withdrawals-${new Date().toISOString().slice(0, 10)}.csv`,
      getCellValue: getWithdrawalCellValue,
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetchTransactions();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  const selectedMerchant = selectedTransaction
    ? (selectedTransaction.merchant?.id ? selectedTransaction.merchant : (selectedTransaction.merchant_id ? merchants.find((m) => m.id === selectedTransaction.merchant_id) : null))
    : null;
  const selectedReseller = selectedTransaction
    ? (selectedTransaction.reseller?.id
      ? selectedTransaction.reseller
      : (selectedTransaction.reseller_id
        ? (resellers.find((r) => r.id === selectedTransaction.reseller_id) || { id: selectedTransaction.reseller_id, name: selectedTransaction.reseller?.name || selectedTransaction.reseller_name })
        : (selectedMerchant?.createdByResellerId
          ? resellers.find((r) => r.id === selectedMerchant.createdByResellerId)
          : null)))
    : null;
  const selectedEngine = null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <ArrowUpFromLine className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">{t("withdrawals")}</h1>
              <p className="text-sm sm:text-base text-slate-500 truncate">
                {txLoading ? (
                  <Skeleton width={200} />
                ) : (
                  `${stats.count} ${t("withdrawals")} • RS${stats.total.toLocaleString()} ${t("total")}`
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="gap-2 text-xs sm:text-sm h-9 sm:h-10"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{t("refresh") || "Refresh"}</span>
            <span className="sm:hidden">↻</span>
          </Button>
          <Button
            variant="outline"
            onClick={handleExportCsv}
            className="gap-2 text-xs sm:text-sm h-9 sm:h-10"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{t("export") || "Export"}</span>
            <span className="sm:hidden">⬇</span>
          </Button>
        </div>
      </div>

      {/* Stats Row - Responsive */}
      {/* 
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap gap-2 sm:gap-3 lg:gap-4">
        <div className="px-3 sm:px-4 py-2 bg-emerald-50 rounded-lg">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] sm:text-xs text-emerald-600/70 font-medium uppercase">{t("successful")}</span>
            <span className="text-emerald-600 font-bold text-sm sm:text-base">{stats.successCount}</span>
          </div>
        </div>
        <div className="px-3 sm:px-4 py-2 bg-amber-50 rounded-lg">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] sm:text-xs text-amber-600/70 font-medium uppercase">{t("pending")}</span>
            <span className="text-amber-600 font-bold text-sm sm:text-base">{stats.pendingCount}</span>
          </div>
        </div>
        <div className="px-3 sm:px-4 py-2 bg-red-50 rounded-lg">
          <div className="flex flex-col gap-0.5">
            <span className="text-[10px] sm:text-xs text-red-600/70 font-medium uppercase">{t("declined") || "Declined"}</span>
            <span className="text-red-600 font-bold text-sm sm:text-base">{stats.declinedCount}</span>
          </div>
        </div>
      </div>
      */}

      {/* Filters  */}
      <TransactionFilters
        filters={filters}
        onFilterChange={(f) => {
          setSearch(f.search);
          setFilters(f);
          setPage(0);
        }}
        onEntityFilterChange={(type, value) => {
          setFilters((prev) => ({ ...prev, [type]: value }));
          setPage(0);
        }}
        merchants={merchants}
        resellers={resellers}
        engines={engines}
        providers={providerOptions}
        onExport={handleExportCsv}
        onReset={() => {
          setFilters({
            search: "",
            type: "all",
            status: "all",
            engine: "all",
            reseller: "all",
            merchant: "all",
            provider: "all",
            currency: "all",
            location: "all",
            dateFrom: undefined,
            dateTo: undefined,
          });
          setMerchantIds([]);
          setSearch("");
          setPage(0);
        }}
      />

      {/* Table - (withdrawals list, mixedTypes for withdrawal icon) */}
      <TransactionTable
        transactions={combinedData}
        isLoading={txLoading}
        onView={setSelectedTransaction}
        isQRCode={false}
        mixedTypes={true}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-xs sm:text-sm text-slate-500 truncate">
            {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, totalTransactions)} {t("of")} {totalTransactions}
          </p>
          <div className="flex items-center justify-center sm:justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              className="h-9 w-9 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs sm:text-sm text-slate-600 px-2 py-1 whitespace-nowrap">
              {t("page")} {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
              className="h-9 w-9 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Transaction Detail Panel */}
      <TransactionDetailPanel
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        engine={selectedEngine}
        reseller={selectedReseller}
        merchant={selectedMerchant}
        isQRCode={selectedTransaction?.type !== "withdrawal"}
      />
    </div>
  );
}
